/** The card the battle's info mode puts on the screen.
 *
 * A leaf, like `InfoPopup`: handed an `InfoCard` and told where the press was,
 * it draws. What it is NOT is `InfoPopup` itself, and the difference is worth
 * stating because reusing that component was the first thing tried.
 *
 * `InfoPopup` is a tooltip: `pointerEvents: "none"`, no scroll, up for a
 * fraction of a second while a right button is held. This card is the opposite
 * of all three. It carries every ability a creature has - a Windcaller's card runs
 * past twenty lines - so it must scroll; it is dismissed by a deliberate press
 * rather than by letting go, so it must take pointer events; and on a phone a
 * 300px box floating at the touch point is a box under the player's own thumb,
 * so there it is a bottom sheet instead of a floating card.
 *
 * Every size here is written `max(<design>px, var(--hm-type-floor))` for the
 * reason `type-floor-sites.spec.ts` gives: a bare size sails under the device
 * type floor at browser zoom, and this panel is mostly small print.
 */

import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";

import type { InfoCard } from "../adventure/info.js";

export interface BattleInfoPanelProps {
  readonly card: InfoCard;
  /** Viewport coordinates of the press that opened it. Ignored on a phone. */
  readonly x: number;
  readonly y: number;
  /** Bottom sheet instead of a floating card. */
  readonly phone: boolean;
  readonly onDismiss: () => void;
}

const OFFSET = 14;
const MARGIN = 8;
const MAX_WIDTH = 320;

const type = (px: number, weight: number): string =>
  `${String(weight)} max(${String(px)}px, var(--hm-type-floor))/1.4 ui-sans-serif, system-ui, sans-serif`;

const S: Readonly<Record<string, CSSProperties>> = {
  head: {
    display: "flex", gap: "8px", alignItems: "flex-start",
    justifyContent: "space-between", marginBottom: "4px",
  },
  title: { font: type(14, 600), color: "#f3ddab" },
  subtitle: { font: type(11, 500), color: "#c3ae86", marginTop: "1px" },
  line: { font: type(11, 400), color: "#e2d4b4", marginTop: "4px" },
  table: { marginTop: "8px", display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 10px" },
  label: { font: type(11, 400), color: "#b8a47e", whiteSpace: "nowrap" },
  value: { font: type(11, 600), color: "#f0e2c0", textAlign: "right" },
  close: {
    font: type(13, 600), color: "#e2d4b4", background: "transparent",
    border: 0, cursor: "pointer", padding: "2px 6px", flex: "0 0 auto",
  },
};

export function BattleInfoPanel(props: BattleInfoPanelProps): JSX.Element {
  const { card, phone } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);

  // Measured before paint, for the reason `InfoPopup` gives: a card drawn at
  // the press and then jumping to its flipped position is a card that flickers.
  useLayoutEffect(() => {
    if (phone) return;
    const el = ref.current;
    if (el === null) return;
    const box = el.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    let left = props.x + OFFSET;
    let top = props.y + OFFSET;
    if (left + box.width > vw - MARGIN) left = props.x - OFFSET - box.width;
    if (top + box.height > vh - MARGIN) top = props.y - OFFSET - box.height;
    setPlace({
      left: Math.max(MARGIN, Math.min(left, vw - MARGIN - box.width)),
      top: Math.max(MARGIN, Math.min(top, vh - MARGIN - box.height)),
    });
  }, [props.x, props.y, card, phone]);

  const host: CSSProperties = phone
    ? {
      position: "fixed", zIndex: 41, left: 0, right: 0, bottom: 0,
      maxHeight: "55vh", overflowY: "auto", padding: "var(--hm-pad)",
      // The sheet is pinned to the bottom edge, so its own bottom corners
      // would be rounded against nothing.
      borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
    }
    : {
      position: "fixed", zIndex: 41, maxWidth: `${String(MAX_WIDTH)}px`,
      maxHeight: "70vh", overflowY: "auto", padding: "var(--hm-pad)",
      left: place?.left ?? -9999,
      top: place?.top ?? -9999,
      visibility: place === null ? "hidden" : "visible",
    };

  return (
    <div ref={ref} className="hm-panel" role="dialog" aria-label={card.title} style={host}>
      <div style={S.head}>
        <div style={{ minWidth: 0 }}>
          <div style={S.title}>{card.title}</div>
          {card.subtitle !== null ? <div style={S.subtitle}>{card.subtitle}</div> : null}
        </div>
        <button type="button" style={S.close} onClick={props.onDismiss} aria-label="Close">x</button>
      </div>

      {card.rows.length > 0 ? (
        <div style={S.table}>
          {card.rows.map((r, i) => (
            <div key={`${String(i)}:${r.label}`} style={{ display: "contents" }}>
              <span style={S.label}>{r.label}</span>
              <span style={S.value}>{r.value}</span>
            </div>
          ))}
        </div>
      ) : null}

      {card.lines.map((l, i) => (
        <div key={`${String(i)}:${l}`} style={S.line}>{l}</div>
      ))}
    </div>
  );
}
